/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';

import { DocumentLoaders } from './types';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { API_BASE, LOADER_BASE, State, getAuthCookie, localAuthHeaders } from '@store/index';
import { useSelector } from 'react-redux';

export type DocumentLoader = {
  load: (
    loader: DocumentLoaders,
    serviceId: string,
    secretIdentifier: string | null,
    onComplete: (value: any[]) => void,
    space?: string,
    modelIds?: string[],
    secondaryIds?: string[]
  ) => void;
  totalPages: number;
  documents: any[];
  loading: boolean;
  error: null | string;
  progress: number;
  rowsLoading: string[];
  prevTab: string | null;
  setDocuments: React.Dispatch<any>;
  addDocumentsToQueue: (
    service_id: string,
    bucketName: string,
    region: string,
    documents: any[],
    prevTab: string | null,
    onComplete: () => void
  ) => void;
  uploadDocuments: (
    service_id: string,
    bucketName: string,
    region: string,
    documents: any[],
    onComplete: () => void
  ) => void;
  addEmbeddingsToQueue: (embeddings: any[]) => void;
  documentsLoaded: number;
  setTotalPages: React.Dispatch<number>;
};

const DELIMITER = '[DELIMITER]';

export const INTERNAL_LOADER_URL = LOCAL
  ? 'http://localhost:3002/api'
  : window.origin.includes('dev')
  ? 'https://flow-loaders.query.dev.telnyx.io:433/api'
  : 'https://flow-loaders.query.prod.telnyx.io:433/api';

export const useDocument = (): DocumentLoader => {
  const userId = useSelector((state: State) => state.auth.user_id);
  const orgId = useSelector((state: State) => state.auth.org_id);

  const [documents, setDocuments] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [error, setError] = useState<null | string>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [prevTab, setPrevTab] = useState<string | null>(null);
  const [rowsLoading, setRowsLoading] = useState<string[]>([]);
  const [documentsLoaded, setDocumentsLoaded] = useState(0);

  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();
  const documentRef = useRef(documents);
  const queryClientRef = useRef(queryClient);

  documentRef.current = documents;
  queryClientRef.current = queryClient;

  const internalAuthHeaders = {
    Authorization: `Bearer ${getAuthCookie()}`,
    'telnyx-ratelimit-user-id': userId,
    'telnyx-ratelimit-org-id': orgId,
    'Content-Type': 'application/json',
  };

  const getLoaderUrl = (
    loader: string,
    service_id: string,
    secretIdentifier: string | null,
    secondaryAuth?: string | null,
    modelIds?: string[],
    secondaryIds?: string[]
  ) => {
    if (loader === 'guru' && modelIds) {
      return `${LOADER_BASE}/${service_id}/loaders/guru/files?secret_identifier=${secretIdentifier}&cardIds=${modelIds?.join(
        ','
      )}&folderIds=${secondaryIds?.join(',')}`;
    }

    if (loader === 'devdocs' && modelIds) {
      return `${INTERNAL_LOADER_URL}/${service_id}/loaders/devdocs/files?products=${modelIds?.join(',')}`;
    }

    if (loader === 'contentful' && secondaryAuth) {
      return `${LOADER_BASE}/${service_id}/loaders/contentful/files?secret_identifier=${secretIdentifier}&space=${secondaryAuth}&modelIds=${modelIds?.join(
        ','
      )}`;
    }

    if (loader === 'intercom')
      return `${LOADER_BASE}/${service_id}/loaders/intercom/files?secret_identifier=${secretIdentifier}`;
    return null;
  };

  const reset = () => {
    setLoading(false);
    setTotalPages(0);
    setDocumentsLoaded(0);
    setDocuments([]);
    setError(null);
    setLoading(false);
    setProgress(0);
  };

  const load = async (
    loader: DocumentLoaders,
    serviceId: string,
    secretIdentifier: string | null,
    onComplete: (value: any[]) => void,
    space?: string,
    modelIds?: string[],
    secondaryIds?: string[]
  ) => {
    try {
      reset();
      setLoading(true);

      const url = getLoaderUrl(loader, serviceId, secretIdentifier, space, modelIds, secondaryIds);
      if (!url) throw new Error('Invalid loader type');

      const response = await fetch(url, {
        method: 'GET',
        headers: LOCAL
          ? localAuthHeaders
          : loader === 'devdocs'
          ? internalAuthHeaders
          : { 'Content-Type': 'application/json', Authorization: `Bearer ${getAuthCookie()}` },
      });

      if (!response?.body) throw new Error('Failed to initiate stream from the server.');

      const reader = response?.body
        ? response.body
            .pipeThrough(new TextDecoderStream()) // eslint-disable-line no-undef
            .getReader()
        : null;

      let streamDone = false;
      let accumulatedData = '';

      while (!streamDone && reader) {
        const { value: unparsedValue, done } = await reader.read();

        if (done) {
          streamDone = true;
          continue;
        }

        accumulatedData += unparsedValue;
        const parts = accumulatedData.split(DELIMITER);

        // Process all but the last part (which may be incomplete)
        for (let i = 0; i < parts.length - 1; i++) {
          const input = parts[i];
          try {
            const { type, value } = JSON.parse(input);

            if (type === 'error') {
              throw new Error(JSON.stringify(value?.detail));
            }

            if (type === 'total_pages') {
              setTotalPages(value);
            }

            if (type === 'document') {
              setDocumentsLoaded((prev) => prev + 1);
            }

            if (type === 'documents_part') {
              setDocumentsLoaded((prev) => prev + value.documents.length);
            }

            if (type === 'documents') {
              setLoading(false);
              setProgress(100);
              setDocumentsLoaded(value.total_length);
              setTotalPages(value.total_length);
              onComplete(value.documents);
            }
          } catch (err) {
            console.error('JSON parse error in chunk:', err);
          }
        }

        // The last part may be incomplete, so keep it for the next iteration
        accumulatedData = parts[parts.length - 1];
      }
    } catch (error: any) {
      console.log('Error streaming documents', error);
      setError(error?.message || 'An unexpected error occurred');
      setLoading(false);
      setProgress(100);
    }
  };

  const uploadDocuments = async (
    service_id: string,
    bucketName: string,
    region: string,
    documents: any[],
    onComplete: () => void
  ) => {
    try {
      setLoading(true);
      const url = `${API_BASE}/${service_id}/buckets/${bucketName}/${region}/objects`;

      const formData = new FormData();

      documents.forEach((file) => {
        formData.append('files', file, file.name);
      });

      const response = await fetch(url, {
        method: 'PUT',
        body: formData,
        headers: LOCAL
          ? {
              Authorization: LOCAL_API_KEY ? `Bearer ${LOCAL_API_KEY}` : '',
              'telnyx-ratelimit-user-id': LOCAL_USER_ID,
              'telnyx-ratelimit-org-id': LOCAL_ORG_ID,
            }
          : { Authorization: `Bearer ${getAuthCookie()}` },
      });

      setRowsLoading((prev) => [...prev, ...documents.map((doc) => doc.name)]);

      const reader = response?.body
        ? response.body
            .pipeThrough(new TextDecoderStream()) // eslint-disable-line no-undef
            .getReader()
        : null;

      let streamDone = false;

      while (!streamDone && reader) {
        try {
          const { value: unparsed_value, done } = await reader.read();

          // if the stream has ended
          if (!unparsed_value || done) {
            setLoading(false);
            setRowsLoading([]);
            setDocuments([]);
            streamDone = true;
            onComplete();
            return queryClientRef.current.invalidateQueries({ queryKey: ['globalFiles'] });
          }

          const input = unparsed_value?.includes(DELIMITER) ? unparsed_value.split(DELIMITER)[0] : unparsed_value;
          const { type, value } = JSON.parse(input);

          if (type === 'progress') {
            setRowsLoading((prev) => prev.filter((id) => id !== value.id));
            setDocuments((prev) => prev.filter((doc) => doc.name !== value.id));
          }

          if (type === 'error') {
            streamDone = true;
            console.log('error', value);
            setError(value?.detail);
            setLoading(false);
            setRowsLoading([]);
            setDocuments([]);

            enqueueSnackbar('Failed to upload file. The queue has been cleared. Please try again shortly.', {
              variant: 'error',
              autoHideDuration: 4000,
            });

            enqueueSnackbar(`Failure Reason: ${value?.detail || value}`, {
              variant: 'error',
              autoHideDuration: 10000,
            });

            queryClientRef.current.invalidateQueries({ queryKey: ['files'] });
          }
        } catch (error) {
          console.log('error', error);
          setLoading(false);
          streamDone = true;
          queryClientRef.current.invalidateQueries({ queryKey: ['files'] });
        }
      }
    } catch (error) {
      setLoading(false);
      console.log('error', error);
      queryClientRef.current.invalidateQueries({ queryKey: ['files'] });
    }
  };

  const addDocumentsToQueue = (
    service_id: string,
    bucketName: string,
    region: string,
    documents: any[],
    prevTab: string | null,
    onComplete: () => void
  ) => {
    setDocuments((prev) => prev.concat(documents));
    uploadDocuments(service_id, bucketName, region, documents, onComplete);
    setPrevTab(prevTab);
  };

  const addEmbeddingsToQueue = (embeddings: any[]) => {
    setDocuments((prev) => prev.concat(embeddings));
  };

  return {
    load,
    totalPages,
    documents,
    loading,
    error,
    progress,
    rowsLoading,
    setDocuments,
    addDocumentsToQueue,
    uploadDocuments,
    addEmbeddingsToQueue,
    prevTab,
    documentsLoaded,
    setTotalPages,
  };
};
